import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { e2eAgentApi } from "./e2eAgentClient";
import type { components } from "./e2eAgentSchema";

export type E2EAgentAdminRun = components["schemas"]["RunOut"];
export type E2EAgentArtifact = components["schemas"]["ArtifactOut"];
export type E2EAgentFailureDiagnostics =
  components["schemas"]["FailureDiagnosticsOut"];
export type E2EAgentRunTraceSummary =
  components["schemas"]["RunTraceSummaryOut"];
export type E2EAgentRunCheckSummary =
  components["schemas"]["RunCheckSummaryOut"];
export type E2EAgentRunCheckDetails =
  components["schemas"]["RunCheckDetailsOut"];
export type PersonalNotificationSettings =
  components["schemas"]["PersonalNotificationSettingsOut"];
export type E2EAgentUsageRun = components["schemas"]["UsageRunOut"];
export type E2EAgentUsageRuns = components["schemas"]["UsageRunsOut"];

const configurationQueryKey = ["e2e-agent", "configuration"] as const;
const personalNotificationsQueryKey = [
  "e2e-agent",
  "notifications",
  "personal",
] as const;
const slackQueryKey = ["e2e-agent-admin", "slack"] as const;
const personalSlackQueryKey = ["e2e-agent", "slack", "personal"] as const;

function ensure<Data>(data: Data | undefined, error: unknown): Data {
  if (error !== undefined) {
    const detail =
      error && typeof error === "object" && "detail" in error
        ? JSON.stringify(error.detail)
        : JSON.stringify(error);
    throw new Error(`E2E-agent request failed: ${detail}`);
  }
  if (data === undefined) throw new Error("E2E-agent response was empty.");
  return data;
}

export function useE2EAgentConfiguration() {
  return useQuery({
    queryKey: configurationQueryKey,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/configuration");
      return ensure(data, error);
    },
    staleTime: 5 * 60_000,
  });
}

export function useE2EAgentAdminRuns() {
  return useQuery({
    queryKey: ["e2e-agent-admin", "runs"],
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/runs", {
        params: { query: { limit: 200 } },
      });
      return ensure(data, error);
    },
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.state.kind !== "terminal")
        ? 5_000
        : 30_000,
  });
}

export function useE2EAgentAdminRun(runId: string) {
  return useQuery({
    queryKey: ["e2e-agent-admin", "run", runId],
    enabled: runId !== "",
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/runs/{run_id}", {
        params: { path: { run_id: runId } },
      });
      return ensure(data, error);
    },
    refetchInterval: (query) =>
      query.state.data?.state.kind === "terminal" ? false : 5_000,
  });
}

export function useE2EAgentUsageRuns(sinceIso: string) {
  return useQuery({
    queryKey: ["e2e-agent-admin", "usage-runs", sinceIso],
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/admin/usage/runs", {
        params: { query: { since: sinceIso } },
      });
      return ensure(data, error);
    },
    // Range changes keep the previous frame on screen instead of flashing.
    placeholderData: (previous) => previous,
    staleTime: 60_000,
  });
}

export function useE2EAgentFailureDiagnostics(runId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["e2e-agent-admin", "run", runId, "failure-diagnostics"],
    enabled: runId !== "" && enabled,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET(
        "/v1/runs/{run_id}/failure-diagnostics",
        { params: { path: { run_id: runId } } },
      );
      return ensure(data, error);
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useE2EAgentRunCheckSummary(runId: string, terminal: boolean) {
  return useQuery({
    queryKey: ["e2e-agent-admin", "run", runId, "check-summary", terminal],
    enabled: runId !== "" && terminal,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET(
        "/v1/runs/{run_id}/check-summary",
        { params: { path: { run_id: runId } } },
      );
      return ensure(data, error);
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useE2EAgentRunCheckDetails(runId: string) {
  return useQuery({
    queryKey: ["e2e-agent-admin", "run", runId, "check-details"],
    enabled: runId !== "",
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET(
        "/v1/runs/{run_id}/check-details",
        { params: { path: { run_id: runId } } },
      );
      return ensure(data, error);
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useE2EAgentRunTraceSummary(runId: string, terminal: boolean) {
  return useQuery({
    queryKey: ["e2e-agent-admin", "run", runId, "trace-summary", terminal],
    enabled: runId !== "",
    queryFn: async () => {
      const { data, error, response } = await e2eAgentApi.GET(
        "/v1/runs/{run_id}/trace-summary",
        { params: { path: { run_id: runId } } },
      );
      if (response.status === 404) return null;
      return ensure(data, error);
    },
    retry: false,
    refetchInterval: (query) =>
      !terminal && query.state.data === null ? 5_000 : false,
    staleTime: terminal ? Number.POSITIVE_INFINITY : 0,
  });
}

export function useE2EAgentAdminArtifactLinks() {
  return useMutation({
    mutationFn: async ({
      runId,
      roles,
    }: {
      runId: string;
      roles: string[];
    }) => {
      const { data, error } = await e2eAgentApi.POST(
        "/v1/runs/{run_id}/artifact-links",
        {
          params: { path: { run_id: runId } },
          body: { roles },
        },
      );
      return ensure(data, error);
    },
  });
}

export function useSlackSettings(enabled = true) {
  return useQuery({
    queryKey: slackQueryKey,
    enabled,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/admin/slack");
      return ensure(data, error);
    },
  });
}

export function usePersonalNotificationSettings() {
  return useQuery({
    queryKey: personalNotificationsQueryKey,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET(
        "/v1/notifications/personal",
      );
      return ensure(data, error);
    },
  });
}

export function useUpdatePersonalNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (emailEnabled: boolean) => {
      const { data, error } = await e2eAgentApi.PUT(
        "/v1/notifications/personal",
        { body: { email_enabled: emailEnabled } },
      );
      return ensure(data, error);
    },
    onSuccess: (settings) =>
      queryClient.setQueryData(personalNotificationsQueryKey, settings),
  });
}

export function usePersonalSlackSettings(enabled = true) {
  return useQuery({
    queryKey: personalSlackQueryKey,
    enabled,
    queryFn: async () => {
      const { data, error } = await e2eAgentApi.GET("/v1/slack/personal");
      return ensure(data, error);
    },
  });
}

export function useUpdatePersonalSlack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data, error } = await e2eAgentApi.PUT("/v1/slack/personal", {
        body: { enabled },
      });
      return ensure(data, error);
    },
    onSuccess: (settings) =>
      queryClient.setQueryData(personalSlackQueryKey, settings),
  });
}

export function useStartSlackOAuth() {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await e2eAgentApi.POST(
        "/v1/admin/slack/oauth/start",
      );
      return ensure(data, error);
    },
  });
}

export function useDisconnectSlack() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await e2eAgentApi.DELETE("/v1/admin/slack");
      if (error !== undefined) ensure(undefined, error);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: slackQueryKey });
      await queryClient.invalidateQueries({ queryKey: personalSlackQueryKey });
    },
  });
}
